import { useAuth } from "@/_core/hooks/useAuth";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { getLoginUrl } from "@/const";
import { useIsMobile } from "@/hooks/useMobile";
import {
  LayoutDashboard,
  LogOut,
  PanelLeft,
  Users,
  Apple,
  Utensils,
  Scale,
  Dumbbell,
  Info,
  GitBranch,
  ShoppingBasket,
  BookOpen,
  ChefHat,
  ShieldCheck,
  TrendingUp,
} from "lucide-react";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { DashboardLayoutSkeleton } from "./DashboardLayoutSkeleton";
import { Button } from "./ui/button";
import { useTranslation } from "@/hooks/useTranslation";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { usePermissions } from "@/_core/hooks/usePermissions";

// ── Navigation structure ────────────────────────────────────────────────────
// Each group has an optional label (shown in expanded sidebar) and a list of
// menu items.  Groups with labelKey === null render without a section heading.

interface MenuItem {
  icon: React.ElementType;
  labelKey: string;
  path: string;
  /** If set, the item is only shown when the user has this permission. */
  requirePermission?: keyof import("@/_core/hooks/usePermissions").Permissions;
}

interface MenuGroup {
  /** i18n key for the group heading (common:app.group.*); null = no heading */
  groupLabelKey: string | null;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    groupLabelKey: null,
    items: [
      { icon: LayoutDashboard, labelKey: "dashboard", path: "/" },
      // Clients: admin/trainer only (regular users see only their own profile via /measurements etc.)
      {
        icon: Users,
        labelKey: "clients",
        path: "/clients",
        requirePermission: "canReadOwnIntake",
      },
      { icon: Scale, labelKey: "measurements", path: "/measurements" },
    ],
  },

  {
    groupLabelKey: "trainingGroup",
    items: [
      {
        icon: Dumbbell,
        labelKey: "attendance",
        path: "/attendance",
        requirePermission: "canReadOwnAttendance",
      },
      {
        icon: TrendingUp,
        labelKey: "strengthProgress",
        path: "/strength-progress",
        requirePermission: "canReadOwnStrengthProgress",
      },
      {
        icon: BookOpen,
        labelKey: "trainingTemplates",
        path: "/training-templates",
        requirePermission: "canWriteStrengthProgress",
      },
    ],
  },

  {
    groupLabelKey: "nutritionGroup",
    items: [
      // Foods: visible to all authenticated users (read-only for regular users)
      {
        icon: Apple,
        labelKey: "foods",
        path: "/foods",
        requirePermission: "canReadFoods",
      },
      { icon: Utensils, labelKey: "dailyIntake", path: "/intake" },
      {
        icon: ShoppingBasket,
        labelKey: "pantry",
        path: "/pantry",
        requirePermission: "canReadOwnPantry",
      },
      // Meal templates: admin/trainer only
      {
        icon: BookOpen,
        labelKey: "mealTemplates",
        path: "/meal-templates",
        requirePermission: "canReadTemplates",
      },
      {
        icon: ChefHat,
        labelKey: "recipes",
        path: "/recipes",
        requirePermission: "canReadRecipes",
      },
    ],
  },

  {
    groupLabelKey: "adminGroup",
    items: [
      {
        icon: ShieldCheck,
        labelKey: "adminManagement",
        path: "/admin",
        requirePermission: "canAccessSystemAdmin",
      },
    ],
  },
];

// Flat list used for active-item lookup (includes all items regardless of permissions)
const allMenuItems: MenuItem[] = menuGroups.flatMap(g => g.items);

const SIDEBAR_WIDTH_KEY = "sidebar-width";
const DEFAULT_WIDTH = 280;
const MIN_WIDTH = 200;
const MAX_WIDTH = 480;

// Declare global constants that will be injected by Vite
declare const __APP_VERSION__: string;
declare const __BUILD_DATE__: string;
declare const __GIT_COMMIT__: string;
declare const __NODE_ENV__: string;

export default function DashboardLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  // Read the sidebar open/collapsed state from the cookie that SidebarProvider
  // writes on every toggle.  This prevents the sidebar from snapping back to
  // "expanded" whenever the user navigates to a new page (which re-mounts
  // DashboardLayout and therefore SidebarProvider).
  const sidebarDefaultOpen = (() => {
    const match = document.cookie
      .split("; ")
      .find(row => row.startsWith("sidebar_state="));
    if (!match) return true; // default: open
    return match.split("=")[1] === "true";
  })();
  const { loading, user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_KEY, sidebarWidth.toString());
  }, [sidebarWidth]);

  if (loading) {
    return <DashboardLayoutSkeleton />;
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <div className="flex flex-col items-center gap-6">
            <h1 className="text-2xl font-semibold tracking-tight text-center">
              {t("common:user:signIn")}
            </h1>
            <p className="text-sm text-muted-foreground text-center max-w-sm">
              {t("common:user:signInDescription")}
            </p>
          </div>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl();
            }}
            size="lg"
            className="w-full shadow-lg hover:shadow-xl transition-all"
          >
            {t("common:user:signIn")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider
      defaultOpen={sidebarDefaultOpen}
      style={
        {
          "--sidebar-width": `${sidebarWidth}px`,
        } as CSSProperties
      }
    >
      <DashboardLayoutContent setSidebarWidth={setSidebarWidth}>
        {children}
      </DashboardLayoutContent>
      <AppFooter />
    </SidebarProvider>
  );
}

type DashboardLayoutContentProps = {
  children: React.ReactNode;
  setSidebarWidth: (width: number) => void;
};

function DashboardLayoutContent({
  children,
  setSidebarWidth,
}: DashboardLayoutContentProps) {
  const { user, logout } = useAuth();
  const permissions = usePermissions();
  const [location, setLocation] = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const activeMenuItem = allMenuItems.find(item => item.path === location);
  const isMobile = useIsMobile();
  const { t, i18n } = useTranslation();

  // Filter menu groups based on user permissions
  const visibleMenuGroups = menuGroups
    .map(group => ({
      ...group,
      items: group.items.filter(
        item => !item.requirePermission || permissions[item.requirePermission]
      ),
    }))
    .filter(group => group.items.length > 0);

  useEffect(() => {
    if (isCollapsed) setIsResizing(false);
  }, [isCollapsed]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      const sidebarLeft = sidebarRef.current?.getBoundingClientRect().left ?? 0;
      const newWidth = e.clientX - sidebarLeft;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH)
        setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    }
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, setSidebarWidth]);

  return (
    <>
      <div className="relative" ref={sidebarRef}>
        <Sidebar
          collapsible="icon"
          className="border-r-0"
          disableTransition={isResizing}
        >
          {/* ── Header ── */}
          <SidebarHeader className="h-16 justify-center">
            <div className="flex items-center gap-3 px-2 transition-all w-full">
              <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring shrink-0"
                aria-label={t("common:toggleNavigation")}
              >
                <PanelLeft className="h-4 w-4 text-muted-foreground" />
              </button>
              {!isCollapsed ? (
                <div className="flex items-center justify-between flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold tracking-tight truncate text-primary">
                      {t("common:app.title")}
                    </span>
                  </div>
                  <LanguageSwitcher />
                </div>
              ) : null}
            </div>
          </SidebarHeader>

          {/* ── Navigation groups ──
              Extra bottom padding (pb-20) ensures the last item is not hidden
              behind the fixed AppFooter (h-16) even when the sidebar is short. */}
          <SidebarContent className="gap-0 pb-20">
            {visibleMenuGroups.map((group, groupIdx) => (
              <SidebarGroup key={groupIdx} className="px-2 py-1">
                {group.groupLabelKey && !isCollapsed && (
                  <SidebarGroupLabel className="text-xs text-muted-foreground/70 uppercase tracking-wider px-2 mb-1">
                    {t(
                      `common:app.group.${group.groupLabelKey}`,
                      group.groupLabelKey
                    )}
                  </SidebarGroupLabel>
                )}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map(item => {
                      const isActive = location === item.path;
                      return (
                        <SidebarMenuItem key={item.path}>
                          <SidebarMenuButton
                            asChild
                            isActive={isActive}
                            tooltip={t(`common:app.${item.labelKey}`)}
                            className="h-10 transition-all font-normal"
                          >
                            <button
                              onClick={() => setLocation(item.path)}
                              className="w-full flex items-center gap-2"
                            >
                              <item.icon
                                className={`h-4 w-4 ${isActive ? "text-primary" : ""}`}
                              />
                              <span>{t(`common:app.${item.labelKey}`)}</span>
                            </button>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>

          {/* ── Footer: user avatar + logout dropdown ──
              Positioned above the fixed AppFooter (h-16) via mb-16. */}
          <SidebarFooter className="p-3 mb-16">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 rounded-lg px-1 py-1 hover:bg-accent/50 transition-colors w-full text-left group-data-[collapsible=icon]:justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-9 w-9 border shrink-0">
                    <AvatarFallback className="text-xs font-medium">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                    <p className="text-sm font-medium truncate leading-none">
                      {user?.name || "-"}
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-1.5">
                      {user?.email || "-"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={logout}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t("common:user.signOut")}</span>
                </DropdownMenuItem>
                {!isCollapsed && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      {t("common:language")}
                    </div>
                    <DropdownMenuItem onClick={() => i18n.changeLanguage("sk")}>
                      🇸🇰 Slovenčina
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => i18n.changeLanguage("en")}>
                      🇬🇧 English
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>

        {/* Resize handle */}
        <div
          className={`absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-primary/20 transition-colors ${isCollapsed ? "hidden" : ""}`}
          onMouseDown={() => {
            if (isCollapsed) return;
            setIsResizing(true);
          }}
          style={{ zIndex: 50 }}
        />
      </div>

      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <div className="flex items-center gap-3">
                <div className="flex flex-col gap-1">
                  <span className="tracking-tight text-foreground">
                    {activeMenuItem
                      ? t(`common:app.${activeMenuItem.labelKey}`)
                      : t("common:app.dashboard")}
                  </span>
                </div>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        )}
        <main className="flex-1 p-4 pb-20">{children}</main>
      </SidebarInset>
    </>
  );
}

// ── Footer Component ─────────────────────────────────────────────────────────
function AppFooter() {
  const { t } = useTranslation();
  const [showVersionDetails, setShowVersionDetails] = useState(false);

  const formatBuildDate = (isoDate: string) => {
    try {
      const date = new Date(isoDate);
      return date.toLocaleDateString("sk-SK", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return isoDate;
    }
  };

  const displayVersion = __APP_VERSION__?.startsWith("v")
    ? __APP_VERSION__
    : `v${__APP_VERSION__ || "0.0.0"}`;

  const displayCommit =
    __GIT_COMMIT__ && __GIT_COMMIT__ !== "unknown"
      ? __GIT_COMMIT__.substring(0, 7)
      : "dev";

  const environment = __NODE_ENV__ === "production" ? "PROD" : "DEV";
  const currentYear = new Date().getFullYear();

  return (
    <>
      {showVersionDetails && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowVersionDetails(false)}
        >
          <div
            className="bg-background rounded-lg shadow-lg max-w-md w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  {t("common:app.title")} - {t("common:versionInfo")}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowVersionDetails(false)}
                  className="h-8 w-8 p-0"
                >
                  ×
                </Button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("common:version")}
                    </p>
                    <p className="text-lg font-mono">{displayVersion}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("common:environment")}
                    </p>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        environment === "PROD"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {environment}
                    </span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    {t("common:buildDate")}
                  </p>
                  <p>
                    {formatBuildDate(
                      __BUILD_DATE__ || new Date().toISOString()
                    )}
                  </p>
                </div>
                {__GIT_COMMIT__ && __GIT_COMMIT__ !== "unknown" && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      {t("common:gitCommit")}
                    </p>
                    <div className="flex items-center gap-2">
                      <GitBranch className="h-4 w-4" />
                      <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                        {__GIT_COMMIT__}
                      </code>
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    {t("common:copyright", { year: currentYear })}
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={() => setShowVersionDetails(false)}>
                  {t("common:close")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="fixed bottom-0 left-0 right-0 h-16 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-30">
        <div className="container mx-auto h-full px-4">
          <div className="flex items-center justify-between h-full">
            <div className="text-sm text-muted-foreground hidden md:block">
              © {currentYear} {t("common:app.title")}.{" "}
              {t("common:allRightsReserved")}
            </div>
            <div className="flex-1 md:hidden">
              <button
                onClick={() => setShowVersionDetails(true)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Info className="h-4 w-4" />
                <span>{displayVersion}</span>
              </button>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowVersionDetails(true)}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors group"
                title={t("common:clickForDetails")}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{displayVersion}</span>
                  {displayCommit !== "dev" && (
                    <span className="text-xs text-muted-foreground font-mono">
                      ({displayCommit})
                    </span>
                  )}
                </div>
                <Info className="h-3 w-3 text-muted-foreground group-hover:text-foreground transition-colors" />
              </button>
              <div
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                  environment === "PROD"
                    ? "bg-green-100 text-green-800 border border-green-200"
                    : "bg-yellow-100 text-yellow-800 border border-yellow-200"
                }`}
              >
                <div
                  className={`h-1.5 w-1.5 rounded-full ${
                    environment === "PROD" ? "bg-green-500" : "bg-yellow-500"
                  }`}
                />
                <span>{environment}</span>
              </div>
              <div className="hidden lg:block text-xs text-muted-foreground">
                {t("common:built")}:{" "}
                {formatBuildDate(__BUILD_DATE__ || new Date().toISOString())}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
